import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isWithinInterval, parseISO } from 'date-fns';
import { pedidosAPI, handleAPIError } from '../../services/api';
import { getNetlifyFunctionUrl } from '../../config/config';
import { supabase } from '../../config/supabase';

// Helper function to get auth headers
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
};

// Thunks para operaciones asíncronas
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data, weekSummary } = await pedidosAPI.getAll(filters);
      return { data, weekSummary };
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const fetchOrdersByWeek = createAsyncThunk(
  'orders/fetchOrdersByWeek',
  async (payload, { rejectWithValue }) => {
    const { year, week, filters = {} } = payload || {};

    if (!year || !week) {
      return rejectWithValue('Temporada o semana no especificada.');
    }

    try {
      const { data, weekSummary } = await pedidosAPI.getByWeek({ year, week }, filters);
      return { data, weekSummary };
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await pedidosAPI.create(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateOrder = createAsyncThunk(
  'orders/updateOrder',
  async ({ id, orderData }, { rejectWithValue }) => {
    try {
      const response = await pedidosAPI.update(id, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteOrder = createAsyncThunk(
  'orders/deleteOrder',
  async (id, { rejectWithValue }) => {
    try {
      await pedidosAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const revertOrderConfirmation = createAsyncThunk(
  'orders/revertOrderConfirmation',
  async (orderId, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const url = getNetlifyFunctionUrl('revert-order-confirmation');
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al revertir confirmación');
      }
      
      const data = await response.json();
      return data.order;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const confirmOrdersManual = createAsyncThunk(
  'orders/confirmOrdersManual',
  async (_, { rejectWithValue }) => {
    try {
      const headers = await getAuthHeaders();
      const url = getNetlifyFunctionUrl('confirm-orders-manual');
      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al confirmar pedidos');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  orders: [],
  loading: false,
  error: null,
  weekSummary: {
    totalOrders: 0,
    totalAmount: 0,
    statusCounts: {},
    priorityCounts: {},
    exportOrders: {
      count: 0,
      totalBoxes: 0,
      totalWeight: 0,
      totalVolume: 0
    },
    nacionalOrders: {
      count: 0,
      boxesByType: {}
    }
  },
  weekRange: {
    start: null,
    end: null
  },
  filters: {
    search: '',
    status: 'all',
    type: 'all',
    priority: 'all',
    customerId: 'all',
    clientType: 'all'
  }
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        status: 'all',
        type: 'all',
        priority: 'all',
        customerId: 'all',
        clientType: 'all'
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data;
        state.weekSummary = action.payload.weekSummary || state.weekSummary;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch orders by week
      .addCase(fetchOrdersByWeek.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByWeek.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data;
        state.weekSummary = action.payload.weekSummary || state.weekSummary;
        state.weekRange = action.payload.weekRange || state.weekRange;
      })
      .addCase(fetchOrdersByWeek.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update order
      .addCase(updateOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex(order => order.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete order
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter(order => order.id !== action.payload);
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Revert order confirmation
      .addCase(revertOrderConfirmation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revertOrderConfirmation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex(order => order.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(revertOrderConfirmation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Confirm orders manual
      .addCase(confirmOrdersManual.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmOrdersManual.fulfilled, (state, action) => {
        state.loading = false;
        // Update estado of confirmed orders to 'Confirmado'
        if (action.payload.orderIds && action.payload.orderIds.length > 0) {
          action.payload.orderIds.forEach(orderId => {
            const index = state.orders.findIndex(order => order.id === orderId);
            if (index !== -1) {
              state.orders[index].estado = 'Confirmado';
            }
          });
        }
      })
      .addCase(confirmOrdersManual.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  clearError
} = ordersSlice.actions;

// Selectors
export const selectAllOrders = (state) => state.orders.orders;
export const selectOrdersLoading = (state) => state.orders.loading;
export const selectOrdersError = (state) => state.orders.error;
export const selectOrdersFilters = (state) => state.orders.filters;

export const selectOrdersForWeek = (state) => {
  const orders = selectAllOrders(state);
  const seasonSelection = state.season?.selected;

  if (!seasonSelection || !seasonSelection.range) {
    return orders;
  }

  const { start, end } = seasonSelection.range;
  const selectedWeekNumber = seasonSelection.week;
  const weekStart = start ? parseISO(start) : null;
  const weekEnd = end ? parseISO(end) : null;

  return orders.filter(order => {
    const matchesSelectedWeek =
      selectedWeekNumber != null &&
      order.deliveryWeek != null &&
      Number(order.deliveryWeek) === Number(selectedWeekNumber);

    if (order.deliveryDate && weekStart && weekEnd) {
      const deliveryDate = parseISO(order.deliveryDate);
      if (!Number.isNaN(deliveryDate?.getTime?.()) && isWithinInterval(deliveryDate, { start: weekStart, end: weekEnd })) {
        return true;
      }
    }

    if (!weekStart || !weekEnd) {
      return matchesSelectedWeek;
    }

    return matchesSelectedWeek;
  });
};

export const selectFilteredOrdersForWeek = (state) => {
  const weekOrders = selectOrdersForWeek(state);
  const filters = selectOrdersFilters(state);
  
  return weekOrders.filter(order => {
    const searchValue = filters.search?.trim().toLowerCase();
    const matchesSearch = !searchValue || 
      order.customerName?.toLowerCase().includes(searchValue) ||
      order.productName?.toLowerCase().includes(searchValue) ||
      order.orderNumber?.toLowerCase().includes(searchValue);
    
    const matchesCustomer = filters.customerId === 'all' || order.customerId?.toString() === filters.customerId;
    
    return matchesSearch && matchesCustomer;
  });
};

export const selectOrderById = (state, orderId) => {
  return state.orders.orders.find(order => order.id === orderId);
};

export const selectWeekSummary = (state) => state.orders.weekSummary;

export const selectConsolidatedProducts = (state) => {
  const weekOrders = selectOrdersForWeek(state);
  const productMap = new Map();

  weekOrders.forEach(order => {
    if (order.detalles && Array.isArray(order.detalles)) {
      order.detalles.forEach(detalle => {
        const productoId = detalle.producto_id || detalle.producto?.id;
        const productoNombre = detalle.producto?.nombre || 'Producto sin nombre';
        const productoCode = detalle.producto?.code || '';
        const cantidadCajas = detalle.cantidad_cajas || 0;
        const precioTotal = detalle.precio_total || 0;

        if (productoId) {
          if (productMap.has(productoId)) {
            const existing = productMap.get(productoId);
            productMap.set(productoId, {
              ...existing,
              totalCajas: existing.totalCajas + cantidadCajas,
              totalMonto: existing.totalMonto + precioTotal
            });
          } else {
            productMap.set(productoId, {
              id: productoId,
              nombre: productoNombre,
              code: productoCode,
              totalCajas: cantidadCajas,
              totalMonto: precioTotal
            });
          }
        }
      });
    }
  });

  return Array.from(productMap.values()).sort((a, b) => b.totalCajas - a.totalCajas);
};

export default ordersSlice.reducer;
